/**
 * screens/chat.tsx — SK Chat (POOL, read-only). Bot-first, escalate-to-human;
 * per-agent concurrency (perAgent). Bot-handling lane + Waiting-for-a-human FIFO
 * + Agents lane (live load squares, busy/available/away). Port of the prototype's
 * Chat surface + _chatVM.
 */

import React from 'react';
import { View, Text, ScrollView } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useQueue } from '../queue/store';
import { fmtDur } from '../queue/format';
import { Mono, TierPill } from '../queue/components/atoms';
import { useResponsive } from '../lib/responsive';
import { SurfaceFill } from '../lib/surfaceLayout';
import { useDataSource } from '../queue/dataSource';
import { useActiveQueue } from '../queue/activeQueue';
import { useChatMonitor } from '../screens/hooks/useMonitor';
import type { ChatState } from '../queue/types';

export default function ChatScreen() {
  const { theme } = useTheme();
  const { colors } = theme;
  const { width } = useResponsive();
  const { state } = useQueue();
  const { isLiveSurface } = useDataSource();
  const { activeQueueId } = useActiveQueue();
  // Phone: stacks + page-scroll. Wide: fill the viewport, lanes scroll internally.
  const narrow = width < 820;

  // Live when wired + a queue is selected; else the in-memory demo (unchanged).
  const useLive = isLiveSurface('chat') && !!activeQueueId;
  const { view } = useChatMonitor(useLive ? activeQueueId : null);
  const now = Date.now();
  // Map the (thinner, privacy-safe) live monitor into the screen's view shape:
  // tokens not names; agents are pool units (load/capacity) not named people.
  const liveCh: ChatState | null = useLive && view ? {
    perAgent: view.agentsLane[0]?.capacity ?? 1,
    longest: `${Math.round(view.stats.longestWaitSeconds)}s`,
    abandoned: view.stats.abandoned,
    bots: view.botHandlingLane.map((c) => ({
      token: c.token, name: `#${c.token}`, channel: String(c.channel ?? ''), intent: String(c.intent ?? ''),
      startAt: now - c.durationSeconds * 1000, escalating: !!c.escalating, feeds: false,
    })),
    waiting: view.waitingForHumanFifo.map((w) => ({
      token: w.token, name: `#${w.token}`, channel: '', intent: '', startAt: now - w.waitedSeconds * 1000,
    })),
    agents: view.agentsLane.map((u) => ({
      // rev 5 dual-state: presence is operational (ONLINE/BREAK/OFFLINE); "busy"
      // is derived from load (at capacity), "away" from a non-ONLINE presence.
      name: u.label,
      status:
        u.presence !== 'ONLINE' ? 'away' : u.load >= u.capacity ? 'busy' : 'available',
      forName: null, intent: `${u.load}/${u.capacity}`, startAt: null,
    })),
  } : null;

  const ch = liveCh ?? state.chat;
  const dur = (t: number) => fmtDur((now - t) / 1000);

  const online = ch.agents.filter(a => a.status !== 'away').length;
  const loadSum = ch.agents.reduce((s, a) => s + (a.status === 'busy' ? 1 : 0), 0);
  const stats = [
    { k: 'Bot handling', v: String(ch.bots.length) },
    { k: 'Waiting', v: String(ch.waiting.length) },
    { k: 'Agents online', v: String(online) },
    { k: 'Capacity', v: loadSum + '/' + online * ch.perAgent },
    { k: 'Longest wait', v: ch.waiting.length ? dur(Math.min(...ch.waiting.map(w => w.startAt))) : '0:00' },
    { k: 'Abandoned', v: String(ch.abandoned) },
  ];

  const card = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 14, overflow: 'hidden' as const };
  const chanChip = { borderWidth: 1, borderColor: colors.line, borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 };
  const feedsChip = { flexDirection: 'row' as const, alignItems: 'center' as const, height: 18, paddingHorizontal: 7, borderRadius: 5, borderWidth: 1, borderColor: colors.lineStrong, backgroundColor: colors.card };
  const escChip = { flexDirection: 'row' as const, alignItems: 'center' as const, height: 18, paddingHorizontal: 7, borderRadius: 5, backgroundColor: colors.ink };

  const panelsRow = narrow
    ? { flexDirection: 'column' as const, gap: 16 }
    : { flexDirection: 'row' as const, gap: 16, flex: 1, minHeight: 0 as const, alignItems: 'stretch' as const };

  const header = (
        <>
        <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 18 }}>
          <View style={{ flex: 1 }}>
            <Mono style={{ fontSize: 11, letterSpacing: 1.5, color: colors.ink3, textTransform: 'uppercase' }}>Client Admin · SK Chat</Mono>
            <Text style={{ fontSize: 26, fontWeight: '700', marginTop: 4, color: colors.ink }}>Chat queue</Text>
            <Text style={{ fontSize: 13.5, color: colors.ink3, marginTop: 4 }}>
              POOL · the bot handles first, then escalates to humans. Each agent takes <Text style={{ fontWeight: '700', color: colors.ink }}>{ch.perAgent}</Text> chat at a time — at the cap, new chats wait.
            </Text>
          </View>
          <TierPill label="TIER 3 · READ-ONLY" />
        </View>

        <View style={{ flexDirection: 'row', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          {stats.map(s => (
            <View key={s.k} style={{ flex: 1, minWidth: 130, backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 13 }}>
              <Mono style={{ fontSize: 21, fontWeight: '600', lineHeight: 21, color: colors.ink }}>{s.v}</Mono>
              <Text style={{ fontSize: 10, color: colors.ink4, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 5 }}>{s.k}</Text>
            </View>
          ))}
        </View>
        </>
  );

  const panels = (
        <View style={panelsRow}>
          {/* bot handling + waiting — two stacked cards; scroll together on wide */}
          <View style={narrow ? { minWidth: 280 } : { flex: 1.4, minWidth: 280, minHeight: 0 }}>
           <ScrollView style={narrow ? undefined : { flex: 1 }} contentContainerStyle={{ gap: 16 }}>
            <View style={card}>
              <LaneHead colors={colors} title="Bot handling · SUKI" note="auto-replies · intake" />
              {ch.bots.map(c => (
                <View key={c.token} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F1F0EC', backgroundColor: c.escalating ? '#FBFAF6' : colors.card }}>
                  <Mono style={{ fontSize: 11, color: colors.ink4, width: 34 }}>#{c.token}</Mono>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Text style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>{c.name}</Text>
                      <View style={chanChip}><Mono style={{ fontSize: 10, color: colors.ink3 }}>{c.channel}</Mono></View>
                      {c.escalating && <View style={escChip}><Mono style={{ fontSize: 10, fontWeight: '600', color: colors.white }}>escalating →</Mono></View>}
                      {c.feeds && <View style={feedsChip}><Mono style={{ fontSize: 10, fontWeight: '600', color: colors.ink3 }}>→ Restaurant line</Mono></View>}
                    </View>
                    <Text style={{ fontSize: 12, color: colors.ink4, marginTop: 2 }}>{c.intent}</Text>
                  </View>
                  <Mono style={{ fontSize: 13, fontWeight: '600', color: colors.ink2 }}>{dur(c.startAt)}</Mono>
                </View>
              ))}
            </View>

            <View style={card}>
              <LaneHead colors={colors} title="Waiting for a human · FIFO" note="all agents at capacity" />
              {ch.waiting.length === 0 ? (
                <Text style={{ padding: 28, textAlign: 'center', color: colors.ink4, fontSize: 13 }}>No chats waiting</Text>
              ) : ch.waiting.map((w, i) => (
                <View key={w.token} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: '#F1F0EC' }}>
                  <Mono style={{ fontSize: 14, fontWeight: '600', color: colors.ink3, width: 24 }}>{i + 1}</Mono>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                      <Text style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>{w.name}</Text>
                      <View style={chanChip}><Mono style={{ fontSize: 10, color: colors.ink3 }}>{w.channel}</Mono></View>
                    </View>
                    <Text style={{ fontSize: 11.5, color: colors.ink4, marginTop: 2 }}>{w.intent} · waiting {dur(w.startAt)}</Text>
                  </View>
                </View>
              ))}
            </View>
           </ScrollView>
          </View>

          {/* agents */}
          <View style={[card, narrow ? { minWidth: 280 } : { flex: 1, minWidth: 280, minHeight: 0 }]}>
            <LaneHead colors={colors} title="Agents" note={`load · ${ch.perAgent} max`} />
            <ScrollView style={narrow ? { maxHeight: 430 } : { flex: 1 }}>
            {ch.agents.map(a => {
              const busy = a.status === 'busy', away = a.status === 'away';
              const dotStyle = busy
                ? { backgroundColor: colors.ink }
                : (away ? { backgroundColor: colors.card, borderWidth: 2, borderColor: colors.ink4, borderStyle: 'dashed' as const } : { backgroundColor: colors.card, borderWidth: 2, borderColor: colors.lineStrong });
              const statusLabel = away ? 'Away' : (busy ? `Busy · ${ch.perAgent}/${ch.perAgent}` : 'Available');
              return (
                <View key={a.name} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 13, borderBottomWidth: 1, borderBottomColor: '#F1F0EC', opacity: away ? 0.55 : 1 }}>
                  <View style={{ width: 34, height: 34, borderRadius: 17, backgroundColor: colors.paper3, alignItems: 'center', justifyContent: 'center' }}>
                    <Text style={{ fontSize: 12, fontWeight: '600', color: colors.ink2 }}>{a.name.split(' ').map(w => w[0]).slice(0, 2).join('')}</Text>
                  </View>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7 }}>
                      <View style={{ width: 9, height: 9, borderRadius: 5, ...dotStyle }} />
                      <Text style={{ fontSize: 13.5, fontWeight: '600', color: colors.ink }}>{a.name}</Text>
                    </View>
                    {busy
                      ? <Text style={{ fontSize: 11.5, color: colors.ink4, marginTop: 2 }}>{a.intent} · {a.forName} · {a.startAt ? dur(a.startAt) : ''}</Text>
                      : <Text style={{ fontSize: 11.5, color: colors.ink4, marginTop: 2 }}>{statusLabel}</Text>}
                  </View>
                  <View style={{ flexDirection: 'row', gap: 3 }}>
                    {Array.from({ length: ch.perAgent }).map((_, i) => (
                      <View key={i} style={{
                        width: 13, height: 13, borderRadius: 3,
                        backgroundColor: (!away && i < (busy ? 1 : 0)) ? colors.ink : colors.card,
                        borderWidth: (!away && i < (busy ? 1 : 0)) ? 0 : 1, borderColor: colors.lineStrong,
                      }} />
                    ))}
                  </View>
                </View>
              );
            })}
            </ScrollView>
          </View>
        </View>
  );

  if (narrow) {
    return (
      <SurfaceFill>
        <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 24 }}>
          {header}
          {panels}
        </ScrollView>
      </SurfaceFill>
    );
  }
  return (
    <SurfaceFill>
      {header}
      {panels}
    </SurfaceFill>
  );
}

function LaneHead({ colors, title, note }: { colors: any; title: string; note: string }) {
  return (
    <View style={{ paddingHorizontal: 16, paddingVertical: 13, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
      <Text style={{ fontSize: 12, fontWeight: '600', textTransform: 'uppercase', letterSpacing: 0.6, color: colors.ink3 }}>{title}</Text>
      <Mono style={{ fontSize: 11, color: colors.ink4 }}>{note}</Mono>
    </View>
  );
}
